"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { DatePicker } from "../ui/date-picker";
import { Loader2, Upload, X, MessageSquare } from "lucide-react";
import { cn } from "../../lib/utils";
import { DocumentManager } from "../planning/DocumentManager";
import { TemplateField } from "../../templates/misstatement-templates";

interface MisstatementFormRendererProps {
  template: any; // MisstatementTemplate
  formData: Record<string, any>;
  onChange: (fieldId: string, value: any) => void;
  onDocumentChange?: (fieldId: string, documents: any[]) => void;
  onTextResponseChange?: (fieldId: string, textResponse: string | null) => void;
  disabled?: boolean;
  planningData?: {
    materiality?: number;
    performanceMateriality?: number;
    clearlyTrivialThreshold?: number;
  };
  // Data sources for dropdowns and multi-selects
  fsLineItems?: Array<{ id: string; label: string }>;
  planningEstimates?: Array<{ id: string; label: string }>;
  rembManagementEstimates?: Array<{ id: string; label: string }>;
  allMisstatements?: Array<{ id: string; label: string }>;
  controlDeficiencies?: Array<{ id: string; label: string }>;
  onMisstatementClick?: (misstatementId: string) => void;
  onDeficiencyClick?: (deficiencyId: string) => void;
  onReviewCommentClick?: (sectionId: string, sectionLabel: string) => void;
}

export default function MisstatementFormRenderer({
  template,
  formData,
  onChange,
  onDocumentChange,
  onTextResponseChange,
  disabled = false,
  planningData = {},
  misstatementId = "",
  fsLineItems = [],
  planningEstimates = [],
  rembManagementEstimates = [],
  allMisstatements = [],
  controlDeficiencies = [],
  onMisstatementClick,
  onDeficiencyClick,
  onReviewCommentClick,
}: MisstatementFormRendererProps) {
  const [abcotdBalanceCache, setAbcotdBalanceCache] = useState<Record<string, number>>({});
  const [loadingAbcotdBalance, setLoadingAbcotdBalance] = useState<Record<string, boolean>>({});
  const [summaryTableValues, setSummaryTableValues] = useState<Record<string, string | number>>({});
  const [projectedTotalPopulation, setProjectedTotalPopulation] = useState<number>(0);

  // Function to calculate balance for a given ABCOTD (note_line)
  const calculateAbcotdBalance = async (noteLine: string): Promise<number> => {
    if (!noteLine || noteLine.trim() === "") return 0;
    
    // Check cache first
    if (abcotdBalanceCache[noteLine] !== undefined) {
      return abcotdBalanceCache[noteLine];
    }

    // Check if already loading
    if (loadingAbcotdBalance[noteLine]) {
      return 0;
    }

    try {
      setLoadingAbcotdBalance(prev => ({ ...prev, [noteLine]: true }));

      // Step 1: Fetch mapping data to get ledger_ids for the selected ABCOTD
      let mappingData: any = null;
      if (window.sharePointAPI && typeof window.sharePointAPI.downloadTrialBalanceMapping === "function") {
        const mappingResult = await window.sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (mappingResult?.success && mappingResult?.data) {
          mappingData = mappingResult.data;
        }
      }

      if (!mappingData) {
        setAbcotdBalanceCache(prev => ({ ...prev, [noteLine]: 0 }));
        return 0;
      }

      // Extract all ledger_ids where note_line matches the selected ABCOTD
      const ledgerIds = new Set<number>();
      for (const [bookName, entries] of Object.entries(mappingData)) {
        if (Array.isArray(entries)) {
          entries.forEach((entry: any) => {
            const entryNoteLine = entry.note_line || entry.noteLine;
            if (entryNoteLine && entryNoteLine.trim() === noteLine.trim()) {
              const ledgerId = entry.ledger_id || entry.ledgerId;
              if (ledgerId) {
                ledgerIds.add(ledgerId);
              }
            }
          });
        }
      }

      if (ledgerIds.size === 0) {
        setAbcotdBalanceCache(prev => ({ ...prev, [noteLine]: 0 }));
        return 0;
      }

      // Step 2: Find the latest date from amount fields in mapping data
      const allDates: string[] = [];
      for (const [bookName, entries] of Object.entries(mappingData)) {
        if (Array.isArray(entries)) {
          entries.forEach((entry: any) => {
            if (entry.amount && typeof entry.amount === 'object') {
              Object.keys(entry.amount).forEach(date => {
                if (!allDates.includes(date)) {
                  allDates.push(date);
                }
              });
            }
          });
        }
      }

      // Parse dates and find the latest
      const parseDate = (dateStr: string): Date => {
        const [day, month, year] = dateStr.split('/');
        return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
      };

      const sortedDates = allDates.sort((a, b) => {
        const dateA = parseDate(a);
        const dateB = parseDate(b);
        return dateB.getTime() - dateA.getTime(); // Descending order
      });

      const latestDate = sortedDates[0];
      if (!latestDate) {
        setAbcotdBalanceCache(prev => ({ ...prev, [noteLine]: 0 }));
        return 0;
      }

      // Step 3: Sum all balances for the ledger_ids on the latest date from amount field
      let totalBalance = 0;
      for (const [bookName, entries] of Object.entries(mappingData)) {
        if (Array.isArray(entries)) {
          entries.forEach((entry: any) => {
            const entryLedgerId = entry.ledger_id || entry.ledgerId;
            if (ledgerIds.has(entryLedgerId) && entry.amount) {
              const amountData = entry.amount;
              if (typeof amountData === 'object' && amountData[latestDate] !== undefined) {
                totalBalance += parseFloat(amountData[latestDate]) || 0;
              }
            }
          });
        }
      }

      // Cache the result
      setAbcotdBalanceCache(prev => ({ ...prev, [noteLine]: totalBalance }));
      return totalBalance;
    } catch (error) {
      console.error(`Failed to calculate balance for ABCOTD ${noteLine}:`, error);
      setAbcotdBalanceCache(prev => ({ ...prev, [noteLine]: 0 }));
      return 0;
    } finally {
      setLoadingAbcotdBalance(prev => {
        const newState = { ...prev };
        delete newState[noteLine];
        return newState;
      });
    }
  };

  // Calculate values for calculated fields
  const calculateValue = (field: TemplateField): number | string => {
    if (field.type !== "calculated" || !field.formula) return "";

    try {
      // Parse formula like "(C * B) / A"
      const formula = field.formula;
      const matches = formula.match(/[A-Z]/g);
      if (!matches) return "";

      // Map variables to field IDs
      const varMap: Record<string, string> = {
        A: "projected_population_A",
        B: "projected_misstatement_B",
        C: "projected_total_population_C",
        D: "projected_value_D",
      };

      let calculatedFormula = formula;
      matches.forEach((varName) => {
        const fieldId = varMap[varName];
        if (fieldId && formData[fieldId] !== undefined) {
          calculatedFormula = calculatedFormula.replace(
            new RegExp(varName, "g"),
            String(formData[fieldId] || 0),
          );
        }
      });

      // Evaluate the formula safely
      const result = Function(`"use strict"; return (${calculatedFormula})`)();
      return typeof result === "number" ? result : parseFloat(result) || 0;
    } catch (error) {
      console.error("Error calculating field value:", error);
      return "";
    }
  };

  // Check if field should be visible
  const isFieldVisible = (field: TemplateField): boolean => {
    if (!field.visibleIf) return true;

    for (const [key, value] of Object.entries(field.visibleIf)) {
      if (formData[key] !== value) {
        return false;
      }
    }
    return true;
  };

  // Get value for summary table row (synchronous version using cached values)
  const getSummaryTableValue = (valueFrom: string): string | number => {
    if (valueFrom.startsWith("planning.")) {
      const key = valueFrom.split(".")[1];
      return planningData[key as keyof typeof planningData] || "-";
    }
    // Handle calculated projected misstatement - must match calculation table
    if (valueFrom === "projected_misstatement_calculated") {
      const A = parseFloat(formData["projected_population_row1"] || "0");
      const B = parseFloat(formData["projected_misstatement_row1"] || "0");
      const C = projectedTotalPopulation;
      if (A > 0 && B > 0 && C > 0) {
        return (C * B) / A;
      }
      return 0;
    }
    // Handle judgmental misstatement calculation - use cached value
    if (valueFrom === "judgmental_misstatement_calculated") {
      return summaryTableValues[valueFrom] ?? 0;
    }
    return formData[valueFrom] || "-";
  };

  // Update summary table values when ABCOTD or auditor value changes
  useEffect(() => {
    const updateJudgmentalMisstatement = async () => {
      const selectedAbcotd = formData["judgmental_abcotd"];
      const auditorValue = parseFloat(formData["judgmental_auditor_value"] || "0");
      
      if (!selectedAbcotd) {
        setSummaryTableValues(prev => ({ ...prev, judgmental_misstatement_calculated: 0 }));
        return;
      }

      // Clear cache for this ABCOTD to force recalculation
      setAbcotdBalanceCache(prev => {
        const newCache = { ...prev };
        delete newCache[selectedAbcotd];
        return newCache;
      });

      const abcotdBalance = await calculateAbcotdBalance(selectedAbcotd);
      const misstatement = abcotdBalance - auditorValue;
      setSummaryTableValues(prev => ({ ...prev, judgmental_misstatement_calculated: misstatement }));
    };

    if (template.type === "judgmental") {
      updateJudgmentalMisstatement();
    }
  }, [formData.judgmental_abcotd, formData.judgmental_auditor_value, template.type]);

  // Update projected Total Population when ABCOTD changes
  useEffect(() => {
    const updateProjectedTotalPopulation = async () => {
      const selectedAbcotd = formData["projected_abcotd"];
      
      if (!selectedAbcotd) {
        setProjectedTotalPopulation(0);
        return;
      }

      // Clear cache for this ABCOTD to force recalculation
      setAbcotdBalanceCache(prev => {
        const newCache = { ...prev };
        delete newCache[selectedAbcotd];
        return newCache;
      });

      const abcotdBalance = await calculateAbcotdBalance(selectedAbcotd);
      setProjectedTotalPopulation(abcotdBalance);
    };

    if (template.type === "projected") {
      updateProjectedTotalPopulation();
    }
  }, [formData.projected_abcotd, template.type]);

  // Auto-save misstatement value from materiality summary when amount changes
  useEffect(() => {
    template.fields.forEach((field) => {
      if (field.type === "summary_table" && field.rows?.[0]) {
        const misstatementValue = getSummaryTableValue(field.rows[0].valueFrom);
        if (misstatementValue !== undefined && misstatementValue !== null && typeof misstatementValue === "number") {
          const misstatementFieldId = template.type === "factual" 
            ? "factual_misstatement"
            : template.type === "judgmental"
            ? "judgmental_misstatement"
            : "projected_misstatement";
          
          // Only update if the value has changed
          if (formData[misstatementFieldId] !== misstatementValue) {
            onChange(misstatementFieldId, misstatementValue);
          }
        }
      }
    });
  }, [formData.factual_amount, formData.judgmental_auditor_value, formData.judgmental_abcotd, summaryTableValues.judgmental_misstatement_calculated, formData.projected_population_row1, formData.projected_misstatement_row1, projectedTotalPopulation, template.type, planningData]);

  // Label row with optional review note button
  const renderFieldLabel = (field: TemplateField, labelText?: string) => (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-sm font-medium text-white">
        {labelText ?? field.label}
      </Label>
      {onReviewCommentClick && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-amber-400 hover:text-amber-300 hover:bg-white/10"
          onClick={() => onReviewCommentClick(field.id, labelText ?? field.label)}
          title="Review notes"
        >
          <MessageSquare className="h-5 w-5" />
        </Button>
      )}
    </div>
  );

  // Render individual field
  const renderField = (field: TemplateField) => {
    if (!isFieldVisible(field)) return null;

    const value = formData[field.id];
    const showDocumentUpload =
      field.document &&
      ((field.documentIf === "yes" && value === "yes") ||
        (field.documentIf === "no" && value === "no") ||
        !field.documentIf);

    switch (field.type) {
      case "number":
        return (
          <div key={field.id} className="space-y-2">
            {renderFieldLabel(field)}
            <Input
              type="number"
              value={value || ""}
              onChange={(e) =>
                onChange(field.id, parseFloat(e.target.value) || 0)
              }
              disabled={disabled}
              className="border-white/10 bg-white/5 text-white"
            />
          </div>
        );

      case "text":
        return (
          <div key={field.id} className="space-y-2">
            {renderFieldLabel(field)}
            <Textarea
              value={value || ""}
              onChange={(e) => onChange(field.id, e.target.value)}
              disabled={disabled}
              className="border-white/10 bg-white/5 text-white min-h-[100px]"
            />
            {showDocumentUpload && onDocumentChange && onTextResponseChange && (
              <div className="mt-3">
                <DocumentManager
                  sectionKey={misstatementId ? `Misstatement_${misstatementId}_${field.id}` : `misstatement-${field.id}`}
                  groupKey="misstatement"
                  itemId={field.id}
                  itemQuestion={field.label}
                  itemTextResponse={formData[`${field.id}_textResponse`] || null}
                  itemDocuments={formData[`${field.id}_documents`] || []}
                  disabled={disabled}
                  onDocumentsChange={(documents) =>
                    onDocumentChange(`${field.id}_documents`, documents)
                  }
                  onTextResponseChange={(textResponse) =>
                    onTextResponseChange(`${field.id}_textResponse`, textResponse)
                  }
                />
              </div>
            )}
          </div>
        );

      case "dropdown":
        const options =
          field.options ||
          (field.source === "fs_line_items"
            ? fsLineItems
            : field.source === "planning_estimates"
              ? planningEstimates
              : field.source === "remb_management_estimates"
                ? rembManagementEstimates
                : []);

        return (
          <div key={field.id} className="space-y-2">
            {renderFieldLabel(field)}
            <Select
              value={value || ""}
              onValueChange={(val) => onChange(field.id, val)}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {Array.isArray(options) && options.length > 0 ? (
                  options.map((opt: any) => {
                    const optValue =
                      typeof opt === "string" ? opt : opt.id || opt.label;
                    const optLabel =
                      typeof opt === "string" ? opt : opt.label || opt.id;
                    // Ensure value is not empty string
                    if (!optValue || optValue === "") {
                      return null;
                    }
                    return (
                      <SelectItem key={optValue} value={optValue}>
                        {optLabel}
                      </SelectItem>
                    );
                  })
                ) : (
                  <div className="px-2 py-1.5 text-sm text-white/60">
                    No options available
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
        );

      case "calendar":
        return (
          <div key={field.id} className="space-y-2">
            {renderFieldLabel(field)}
            <DatePicker
              value={value ?? ""}
              onChange={(v) => onChange(field.id, v || null)}
              disabled={disabled}
              placeholder="Pick a date"
            />
          </div>
        );

      case "yes_no":
        return (
          <div key={field.id} className="space-y-2">
            {renderFieldLabel(field)}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={value === "yes" ? "default" : "outline"}
                onClick={() => onChange(field.id, "yes")}
                disabled={disabled}
                className={
                  value === "yes"
                    ? "bg-blue-500 text-white"
                    : "border-white/10 text-white"
                }
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={value === "no" ? "default" : "outline"}
                onClick={() => onChange(field.id, "no")}
                disabled={disabled}
                className={
                  value === "no"
                    ? "bg-blue-500 text-white"
                    : "border-white/10 text-white"
                }
              >
                No
              </Button>
            </div>
            {showDocumentUpload && onDocumentChange && (
              <div className="mt-3">
                <DocumentManager
                  sectionKey={misstatementId ? `Misstatement_${misstatementId}_${field.id}` : `misstatement-${field.id}`}
                  groupKey="misstatement"
                  itemId={field.id}
                  itemQuestion={field.label}
                  itemTextResponse={formData[`${field.id}_textResponse`] || null}
                  itemDocuments={formData[`${field.id}_documents`] || []}
                  disabled={disabled}
                  onDocumentsChange={(documents) =>
                    onDocumentChange(`${field.id}_documents`, documents)
                  }
                  onTextResponseChange={(textResponse) =>
                    onTextResponseChange?.(`${field.id}_textResponse`, textResponse)
                  }
                />
              </div>
            )}
          </div>
        );

      case "multi_select_search":
        const sourceData =
          field.source === "all_misstatements"
            ? allMisstatements
            : field.source === "control_deficiencies"
              ? controlDeficiencies
              : [];

        return (
          <div key={field.id} className="space-y-2">
            {renderFieldLabel(field)}
            <div className="space-y-2">
              {Array.isArray(value) &&
                value.map((item: string) => {
                  const isMisstatement = field.source === "all_misstatements";
                  const isDeficiency = field.source === "control_deficiencies";
                  const handleClick = () => {
                    if (isMisstatement && onMisstatementClick) {
                      onMisstatementClick(item);
                    } else if (isDeficiency && onDeficiencyClick) {
                      onDeficiencyClick(item);
                    }
                  };
                  
                  return (
                    <div
                      key={item}
                      className="flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2"
                    >
                      <span 
                        className={`text-sm text-white ${(isMisstatement && onMisstatementClick) || (isDeficiency && onDeficiencyClick) ? 'cursor-pointer hover:text-blue-400 hover:underline' : ''}`}
                        onClick={handleClick}
                      >
                        {item}
                      </span>
                      {!disabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newValue = (value || []).filter(
                              (v: string) => v !== item,
                            );
                            onChange(field.id, newValue);
                          }}
                          className="h-6 w-6 p-0 text-white/70 hover:text-white"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              {!disabled && (
                <Select
                  value={undefined}
                  onValueChange={(val) => {
                    if (!val || val === "") return;
                    const currentValue = value || [];
                    if (!currentValue.includes(val)) {
                      onChange(field.id, [...currentValue, val]);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Add item..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceData
                      .filter(
                        (item: any) => {
                          const itemValue = typeof item === "string" ? item : item.id;
                          return itemValue && itemValue !== "" && !(value || []).includes(itemValue);
                        }
                      )
                      .map((item: any) => {
                        const itemValue =
                          typeof item === "string" ? item : item.id;
                        const itemLabel =
                          typeof item === "string" ? item : item.label || item.id;
                        // Ensure value is not empty string
                        if (!itemValue || itemValue === "") {
                          return null;
                        }
                        return (
                          <SelectItem key={itemValue} value={itemValue}>
                            {itemLabel}
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        );

      case "calculated":
        const calculatedValue = calculateValue(field);
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-sm font-medium text-white">
              {field.label}
            </Label>
            <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white">
              {calculatedValue}
            </div>
          </div>
        );

      case "static":
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-sm font-medium text-white">
              {field.label}
            </Label>
            <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white">
              {field.value || "-"}
            </div>
          </div>
        );

      case "summary_table":
        // Get misstatement value from the first row (which should be the misstatement amount)
        const misstatementValue = field.rows?.[0]?.valueFrom 
          ? getSummaryTableValue(field.rows[0].valueFrom)
          : 0;
        
        // Auto-save misstatement value when it changes (using useEffect outside switch)
        const misstatementFieldId = template.type === "factual" 
          ? "factual_misstatement"
          : template.type === "judgmental"
          ? "judgmental_misstatement"
          : "projected_misstatement";
        
        return (
          <div key={field.id} className="space-y-2">
            {renderFieldLabel(field, "Materiality Summary")}
            <Card className="border-white/10 bg-white/5">
              <CardContent className="p-4">
                <table className="w-full">
                  <tbody>
                    {field.rows?.map((row, idx) => (
                      <tr key={idx} className="border-b border-white/10 last:border-b-0">
                        <td className="py-2 text-sm font-medium text-white/80">
                          {row.label}
                        </td>
                        <td className="py-2 text-right text-sm text-white">
                          {typeof getSummaryTableValue(row.valueFrom) === "number"
                            ? getSummaryTableValue(row.valueFrom).toLocaleString()
                            : getSummaryTableValue(row.valueFrom)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        );

      case "table":
        // Check if this is the calculation table (has multiple rows)
        const isCalculationTable = field.id === "projected_calculation_table";
        const rows = isCalculationTable ? 2 : 1;
        
        return (
          <div key={field.id} className="space-y-2">
            {renderFieldLabel(field)}
            <Card className="border-white/10 bg-white/5">
              <CardContent className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/20">
                        {field.columns?.map((col) => (
                          <th
                            key={col.id}
                            className="px-4 py-2 text-left text-sm font-medium text-white/80"
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: rows }).map((_, rowIndex) => (
                        <tr key={rowIndex}>
                          {field.columns?.map((col) => {
                            // For calculation table, handle row-specific logic
                            let colValue: any;
                            let isReadonly = false;
                            let isCalculated = false;
                            
                            if (isCalculationTable) {
                              if (rowIndex === 0) {
                                // First row: user enters all values
                                if (col.id === "projected_particulars") {
                                  colValue = formData["projected_particulars_row1"] || "";
                                } else if (col.id === "projected_population") {
                                  colValue = formData["projected_population_row1"] || "";
                                } else if (col.id === "projected_misstatement") {
                                  colValue = formData["projected_misstatement_row1"] || "";
                                }
                              } else {
                                // Second row: Particulars is "Total Population", Population is calculated from ABCOTD, Misstatement is calculated
                                if (col.id === "projected_particulars") {
                                  colValue = "Total Population";
                                  isReadonly = true;
                                } else if (col.id === "projected_population") {
                                  colValue = projectedTotalPopulation;
                                  isReadonly = true;
                                  isCalculated = true;
                                } else if (col.id === "projected_misstatement") {
                                  // Calculate: (C * B) / A
                                  // A = Population from row 1
                                  // B = Misstatement from row 1
                                  // C = Total Population (calculated from ABCOTD)
                                  const A = parseFloat(formData["projected_population_row1"] || "0");
                                  const B = parseFloat(formData["projected_misstatement_row1"] || "0");
                                  const C = projectedTotalPopulation;
                                  if (A > 0 && B > 0 && C > 0) {
                                    colValue = (C * B) / A;
                                    isCalculated = true;
                                  } else {
                                    colValue = "";
                                    isCalculated = true;
                                  }
                                }
                              }
                            } else {
                              // Regular table (single row)
                              colValue = formData[col.id] || "";
                            }
                            
                            return (
                              <td key={col.id} className="px-4 py-2">
                                {isCalculated ? (
                                  <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white">
                                    {colValue ? colValue.toLocaleString() : "-"}
                                  </div>
                                ) : isReadonly ? (
                                  <Input
                                    value={colValue}
                                    disabled
                                    className="border-white/10 bg-white/5 text-white"
                                  />
                                ) : col.type === "number" ? (
                                  <Input
                                    type="number"
                                    value={colValue}
                                    onChange={(e) => {
                                      const fieldId = isCalculationTable && rowIndex === 0
                                        ? (col.id === "projected_particulars" ? "projected_particulars_row1" :
                                           col.id === "projected_population" ? "projected_population_row1" :
                                           "projected_misstatement_row1")
                                        : isCalculationTable && rowIndex === 1 && col.id === "projected_population"
                                        ? "projected_population_row2"
                                        : col.id;
                                      onChange(fieldId, parseFloat(e.target.value) || 0);
                                    }}
                                    disabled={disabled}
                                    className="border-white/10 bg-white/5 text-white"
                                  />
                                ) : col.type === "text" ? (
                                  <Textarea
                                    value={colValue}
                                    onChange={(e) => {
                                      const fieldId = isCalculationTable && rowIndex === 0 && col.id === "projected_particulars"
                                        ? "projected_particulars_row1"
                                        : col.id;
                                      onChange(fieldId, e.target.value);
                                    }}
                                    disabled={disabled}
                                    className="border-white/10 bg-white/5 text-white min-h-[60px]"
                                  />
                                ) : col.type === "dropdown" ? (
                                  <Select
                                    value={colValue || ""}
                                    onValueChange={(val) => onChange(col.id, val)}
                                    disabled={disabled}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder={`Select ${col.label.toLowerCase()}`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {col.options
                                        ? col.options.map((opt) => (
                                            <SelectItem key={opt} value={opt}>
                                              {opt}
                                            </SelectItem>
                                          ))
                                        : col.source === "fs_line_items"
                                          ? fsLineItems.map((item: any) => {
                                              const itemValue = typeof item === "string" ? item : item.id;
                                              const itemLabel = typeof item === "string" ? item : item.label || item.id;
                                              if (!itemValue || itemValue === "") return null;
                                              return (
                                                <SelectItem key={itemValue} value={itemValue}>
                                                  {itemLabel}
                                                </SelectItem>
                                              );
                                            })
                                          : col.source === "remb_management_estimates"
                                            ? rembManagementEstimates.map((item: any) => {
                                                const itemValue = typeof item === "string" ? item : item.id;
                                                const itemLabel = typeof item === "string" ? item : item.label || item.id;
                                                if (!itemValue || itemValue === "") return null;
                                                return (
                                                  <SelectItem key={itemValue} value={itemValue}>
                                                    {itemLabel}
                                                  </SelectItem>
                                                );
                                              })
                                            : null}
                                    </SelectContent>
                                  </Select>
                                ) : col.type === "calendar" ? (
                                  <DatePicker
                                    value={colValue ?? ""}
                                    onChange={(v) => onChange(col.id, v || null)}
                                    disabled={disabled}
                                    placeholder="Pick a date"
                                  />
                                ) : (
                                  <Input
                                    value={colValue}
                                    disabled
                                    className="border-white/10 bg-white/5 text-white"
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return (
          <div key={field.id} className="text-white/60">
            Unsupported field type: {field.type}
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {template.fields.map((field) => renderField(field))}
    </div>
  );
}

